import { useEffect, useMemo, useState } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { CalendarDate, XClose } from "@untitledui/icons"
import { toast } from "sonner"
import { useTeam } from "@/lib/api/use-teams"
import { teamsApi } from "@/lib/api/teams"
import { queryKeys } from "@/lib/api/query-keys"
import type { TeamSettingsMetadata } from "@/lib/api/types"
import { Roles } from "@/lib/team/roles"
import { useCurrentRole } from "@/lib/team/use-current-role"
import { useAuth } from "@/lib/auth/auth-context"
import { cx } from "@/lib/utils/cx"

// Working hours + booking rules for the calendar and the customer's booking
// link. Defaults are preloaded (a shop with no config is bookable Mon–Fri
// 8–5, 30-min slots, 2 cars per slot) — this page tunes, it doesn't enable.

interface SchedulingConfig {
  openDays: number[]
  startHour: number
  endHour: number
  slotMinutes: number
  capacityPerSlot: number
  leadTimeHours: number
  windowDays: number
  blockedDates: string[]
  inspectionEnabled: boolean
  dropoffEnabled: boolean
  remindersEnabled: boolean
}

const DEFAULTS: SchedulingConfig = {
  openDays: [1, 2, 3, 4, 5],
  startHour: 8,
  endHour: 17,
  slotMinutes: 30,
  capacityPerSlot: 2,
  leadTimeHours: 2,
  windowDays: 21,
  blockedDates: [],
  inspectionEnabled: true,
  dropoffEnabled: true,
  remindersEnabled: true,
}

const WEEKDAYS = [
  { d: 1, label: "Mon" },
  { d: 2, label: "Tue" },
  { d: 3, label: "Wed" },
  { d: 4, label: "Thu" },
  { d: 5, label: "Fri" },
  { d: 6, label: "Sat" },
  { d: 0, label: "Sun" },
]

const hourLabel = (h: number) =>
  new Date(2026, 0, 1, h).toLocaleTimeString("en-US", { hour: "numeric" })

export function BookingCalendarSettings({ teamId }: { teamId: string }) {
  const { data: team, isLoading } = useTeam(teamId)
  const qc = useQueryClient()
  const [saving, setSaving] = useState(false)
  // Same manager gate as Goals & rewards — working hours and capacity shape
  // what real customers can book; an estimator can look, not change.
  const role = useCurrentRole()
  const { user } = useAuth()
  const canManage =
    !!user?.admin ||
    role === Roles.Director ||
    role === Roles.GeneralManager ||
    role === Roles.ServiceManager
  const [form, setForm] = useState<SchedulingConfig>(DEFAULTS)
  const [initial, setInitial] = useState<SchedulingConfig>(DEFAULTS)
  const [dateDraft, setDateDraft] = useState("")

  useEffect(() => {
    if (!team) return
    const meta = (team.metadata ?? {}) as TeamSettingsMetadata & {
      scheduling?: Partial<SchedulingConfig>
    }
    const merged = { ...DEFAULTS, ...(meta.scheduling ?? {}) }
    setForm(merged)
    setInitial(merged)
  }, [team])

  const dirty = useMemo(
    () => JSON.stringify(form) !== JSON.stringify(initial),
    [form, initial],
  )

  const save = async () => {
    if (!team || saving) return
    setSaving(true)
    try {
      // Read-modify-write — never clobber the other metadata blocks.
      await teamsApi.updateSettings(Number(teamId), {
        metadata: { ...(team.metadata as TeamSettingsMetadata), scheduling: form },
      })
      qc.invalidateQueries({ queryKey: queryKeys.teams.detail(teamId) })
      qc.invalidateQueries({ queryKey: queryKeys.appointments.all })
      setInitial(form)
      toast.success("Booking calendar updated")
    } catch {
      toast.error("Couldn't save. Try again.")
    } finally {
      setSaving(false)
    }
  }

  if (isLoading) {
    return <div className="h-64 rounded-2xl bg-white border border-zinc-200/70 animate-pulse" />
  }

  return (
    <div className="space-y-5 max-w-2xl">
      <div className="rounded-2xl border border-zinc-200/80 bg-white p-5 md:p-6">
        <div className="flex items-start gap-3">
          <div className="size-10 rounded-xl bg-gradient-to-br from-sky-500 to-sky-700 grid place-items-center shrink-0 shadow-md shadow-sky-600/25">
            <CalendarDate className="size-5 text-white" aria-hidden />
          </div>
          <div className="min-w-0">
            <h2 className="text-[16px] font-bold text-zinc-900">Working hours &amp; bookings</h2>
            <p className="text-[13px] text-zinc-500 mt-1 leading-relaxed">
              Customers book themselves from the follow-up link — these rules decide
              what they can pick. Already set up with sensible defaults.
            </p>
            {!canManage && (
              <p className="mt-1.5 text-[11.5px] text-zinc-500">
                Only a manager can change these settings.
              </p>
            )}
          </div>
        </div>

        {/* One native gate for every control inside — a non-manager sees the
            configuration but can't operate it. */}
        <fieldset disabled={!canManage} className="min-w-0 disabled:opacity-70">

        {/* Open days */}
        <div className="mt-5">
          <p className="text-[10.5px] font-bold uppercase tracking-[0.12em] text-zinc-400 mb-2">
            Open days
          </p>
          <div className="flex gap-1.5 flex-wrap">
            {WEEKDAYS.map(({ d, label }) => {
              const on = form.openDays.includes(d)
              return (
                <button
                  key={d}
                  type="button"
                  onClick={() =>
                    setForm((f) => ({
                      ...f,
                      openDays: on
                        ? f.openDays.filter((x) => x !== d)
                        : [...f.openDays, d],
                    }))
                  }
                  className={cx(
                    "h-9 px-3.5 rounded-lg text-[12.5px] font-semibold transition-colors",
                    on
                      ? "bg-zinc-900 text-white"
                      : "bg-white border border-zinc-200 text-zinc-400 hover:text-zinc-700",
                  )}
                >
                  {label}
                </button>
              )
            })}
          </div>
        </div>

        {/* Hours + slot + capacity */}
        <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Field label="Opens">
            <Select
              value={form.startHour}
              onChange={(v) => setForm((f) => ({ ...f, startHour: v }))}
              options={Array.from({ length: 7 }, (_, i) => i + 6)}
              format={hourLabel}
            />
          </Field>
          <Field label="Closes">
            <Select
              value={form.endHour}
              onChange={(v) => setForm((f) => ({ ...f, endHour: v }))}
              options={Array.from({ length: 8 }, (_, i) => i + 14)}
              format={hourLabel}
            />
          </Field>
          <Field label="Slot length">
            <Select
              value={form.slotMinutes}
              onChange={(v) => setForm((f) => ({ ...f, slotMinutes: v }))}
              options={[15, 30, 60]}
              format={(v) => `${v} min`}
            />
          </Field>
          <Field label="Cars per slot">
            <Select
              value={form.capacityPerSlot}
              onChange={(v) => setForm((f) => ({ ...f, capacityPerSlot: v }))}
              options={[1, 2, 3, 4]}
              format={(v) => String(v)}
            />
          </Field>
          <Field label="No bookings sooner than">
            <Select
              value={form.leadTimeHours}
              onChange={(v) => setForm((f) => ({ ...f, leadTimeHours: v }))}
              options={[0, 2, 4, 24]}
              format={(v) => (v === 0 ? "Anytime" : v === 24 ? "1 day out" : `${v}h out`)}
            />
          </Field>
          <Field label="Book up to">
            <Select
              value={form.windowDays}
              onChange={(v) => setForm((f) => ({ ...f, windowDays: v }))}
              options={[7, 14, 21, 30]}
              format={(v) => `${v} days out`}
            />
          </Field>
        </div>

        {/* Visit types */}
        <div className="mt-5">
          <p className="text-[10.5px] font-bold uppercase tracking-[0.12em] text-zinc-400 mb-2">
            Visit types customers can pick
          </p>
          <div className="flex gap-1.5">
            <ToggleChip
              on={form.dropoffEnabled}
              label="Drop-off"
              onClick={() =>
                setForm((f) => ({ ...f, dropoffEnabled: !f.dropoffEnabled }))
              }
            />
            <ToggleChip
              on={form.inspectionEnabled}
              label="Quick look"
              onClick={() =>
                setForm((f) => ({ ...f, inspectionEnabled: !f.inspectionEnabled }))
              }
            />
          </div>
        </div>

        {/* Reminders — the day-before text that cuts no-shows. */}
        <div className="mt-5">
          <p className="text-[10.5px] font-bold uppercase tracking-[0.12em] text-zinc-400 mb-2">
            Reminders
          </p>
          <div className="flex items-start justify-between gap-4">
            <p className="text-[12.5px] text-zinc-500 leading-snug max-w-sm">
              Text each customer the day before their visit — sent inside quiet
              hours, automatically.
            </p>
            <ToggleChip
              on={form.remindersEnabled}
              label="Day-before text"
              onClick={() =>
                setForm((f) => ({ ...f, remindersEnabled: !f.remindersEnabled }))
              }
            />
          </div>
        </div>

        {/* Blocked dates */}
        <div className="mt-5">
          <p className="text-[10.5px] font-bold uppercase tracking-[0.12em] text-zinc-400 mb-2">
            Closed dates (holidays, vacation)
          </p>
          <div className="flex items-center gap-2">
            <input
              type="date"
              value={dateDraft}
              onChange={(e) => setDateDraft(e.target.value)}
              aria-label="Add a closed date"
              className="h-9 px-3 rounded-lg border border-zinc-200 text-[13px] text-zinc-700 focus:outline-none focus:ring-2 focus:ring-zinc-900/10"
            />
            <button
              type="button"
              disabled={!dateDraft || form.blockedDates.includes(dateDraft)}
              onClick={() => {
                setForm((f) => ({
                  ...f,
                  blockedDates: [...f.blockedDates, dateDraft].sort(),
                }))
                setDateDraft("")
              }}
              className="h-9 px-3 rounded-lg bg-zinc-900 text-white text-[12.5px] font-semibold disabled:opacity-40"
            >
              Add
            </button>
          </div>
          {form.blockedDates.length > 0 && (
            <div className="mt-2 flex gap-1.5 flex-wrap">
              {form.blockedDates.map((d) => (
                <span
                  key={d}
                  className="inline-flex items-center gap-1 rounded-lg bg-zinc-100 px-2 py-1 text-[12px] font-medium text-zinc-700"
                >
                  {new Date(`${d}T12:00:00`).toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                  })}
                  <button
                    type="button"
                    aria-label={`Remove ${d}`}
                    onClick={() =>
                      setForm((f) => ({
                        ...f,
                        blockedDates: f.blockedDates.filter((x) => x !== d),
                      }))
                    }
                    className="text-zinc-400 hover:text-zinc-700"
                  >
                    <XClose className="size-3" aria-hidden />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>
        </fieldset>
      </div>

      {dirty && canManage && (
        <div className="sticky bottom-4 rounded-2xl border border-zinc-200 bg-white shadow-lg px-4 py-3 flex items-center justify-between gap-3">
          <p className="text-[13px] text-zinc-600">Unsaved changes</p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setForm(initial)}
              className="h-9 px-3 rounded-xl text-[12.5px] font-semibold text-zinc-600 hover:bg-zinc-50"
            >
              Discard
            </button>
            <button
              type="button"
              disabled={saving}
              onClick={save}
              className="h-9 px-4 rounded-xl bg-zinc-900 text-white text-[12.5px] font-semibold hover:bg-black disabled:opacity-50"
            >
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-[11px] font-semibold text-zinc-500 mb-1">{label}</span>
      {children}
    </label>
  )
}

function Select<T extends number>({
  value,
  onChange,
  options,
  format,
}: {
  value: T
  onChange: (v: T) => void
  options: number[]
  format: (v: number) => string
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(Number(e.target.value) as T)}
      className="w-full h-9 px-2.5 rounded-lg border border-zinc-200 bg-white text-[13px] text-zinc-800 focus:outline-none focus:ring-2 focus:ring-zinc-900/10"
    >
      {options.map((o) => (
        <option key={o} value={o}>
          {format(o)}
        </option>
      ))}
    </select>
  )
}

function ToggleChip({
  on,
  label,
  onClick,
}: {
  on: boolean
  label: string
  onClick: () => void
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      onClick={onClick}
      className={cx(
        "h-9 px-3.5 rounded-lg text-[12.5px] font-semibold transition-colors",
        on
          ? "bg-emerald-600 text-white"
          : "bg-white border border-zinc-200 text-zinc-400 hover:text-zinc-700",
      )}
    >
      {label}
    </button>
  )
}
